import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import validator from 'validator';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCharts } from '../redux/slices/chartsSlice';
import { fetchPlays } from '../redux/slices/playsSlice';
import { fetchOrganizerCompanies } from '../redux/slices/organizersSlice';
import { fetchArtists } from '../redux/slices/artistsSlice';
import { createEvent, deleteEvent, updateEvent } from '../redux/slices/eventsSlice';
import { PromotionType } from '../redux/slices/cartSlice';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import SortableItemCast from './SortableItemCast';
import SortableItemAttribute from './SortableItemAttribute';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import FormGroup from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningDialog from './WarningDialog';
import FileDropzone from './FileDropzone';
import LanguageSelector from './LanguageSelector';
import api from '../api';
import { MAX_IMAGE_SIZE_1MB } from './../constants';
import { ACCEPTED_FORMATS } from './../constants';
import { nanoid } from 'nanoid';
import { DEFAULT_LANGUAGE } from '../utils/localeUtils.js';

export const EventsDrawerType = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  COPY: 'COPY',
};

const CATEGORY_DESCRIPTION_MAX_LENGTH = 160;

function EventsDrawer({ open, type = EventsDrawerType.CREATE, event, onClose }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openPlay, setOpenPlay] = useState(false);
  const [playChanged, setPlayChanged] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState('ro');
  const [selectedChart, setSelectedChart] = useState('');
  const [selectedPlay, setSelectedPlay] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSeatsioChartVisible, setIsSeatsioChartVisible] = useState(true);
  const [hasEventDate, setHasEventDate] = useState(true);
  const [onePlusOne, setOnePlusOne] = useState(false);
  const [index, setIndex] = useState(true);
  const [threePlusOne, setThreePlusOne] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [price, setPrice] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [tickets, setTickets] = useState([]);
  const [warningOpen, setWarningOpen] = useState(false);
  const [premiere, setPremiere] = useState(false);
  const [nationalPremiere, setNationalPremiere] = useState(false);
  const [secondRepresentation, setSecondRepresentation] = useState(false);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [playName, setPlayName] = useState('');
  const [playNameEn, setPlayNameEn] = useState('');
  const [playNameDe, setPlayNameDe] = useState('');
  const [playNameHu, setPlayNameHu] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionDe, setDescriptionDe] = useState('');
  const [descriptionHu, setDescriptionHu] = useState('');
  const [selectedPortraitUrl, setSelectedPortraitUrl] = useState('');
  const [selectedCoverUrl, setSelectedCoverUrl] = useState('');
  const [selectedPortrait, setSelectedPortrait] = useState(null);
  const [selectedCover, setSelectedCover] = useState(null);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [role, setRole] = useState('');
  const [hasPause, setHasPause] = useState(false);
  const [length, setLength] = useState('');
  const [attributeName, setAttributeName] = useState('');
  const [attributeValue, setAttributeValue] = useState('');
  const [attributes, setAttributes] = useState([]);

  const playNameMap = {
    ro: [playName, setPlayName],
    en: [playNameEn, setPlayNameEn],
    de: [playNameDe, setPlayNameDe],
    hu: [playNameHu, setPlayNameHu],
  };

  const [playNameValue, setPlayNameValue] = playNameMap[selectedLanguage] ?? ['', () => {}];

  const descriptionMap = {
    ro: [description, setDescription],
    en: [descriptionEn, setDescriptionEn],
    de: [descriptionDe, setDescriptionDe],
    hu: [descriptionHu, setDescriptionHu],
  };

  const [descriptionValue, setDescriptionValue] = descriptionMap[selectedLanguage] ?? ['', () => {}];

  const fetchCategoryTypes = () => {
    api
      .get(`/categoryPricings/types`)
      .then((res) => {
        setCategoryTypes(res.data.data);
      })
      .catch(() => {
        toast.error(t('An error occured, please refresh the page and try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setCategoryTypes([]);
      });
  };

  const artists = useSelector((state) => state.artists.data);

  const artistsOptions = useMemo(
    () =>
      artists.map(({ id, first_name, last_name, profile_image_url }) => ({
        id,
        label: `${first_name} ${last_name}`,
        profile_image_url,
      })),
    [artists]
  );

  const chartsData = useSelector(({ charts: { data } }) =>
    data.map(({ id, name, city, venue_name, categories }) => ({
      id,
      name: `${city} - ${venue_name} - ${name}`,
      categories,
    }))
  );
  const chartsDataLoading = useSelector(({ charts: { isLoading } }) => isLoading);

  const selectedChartCategories = useMemo(() => {
    const venue = chartsData.find(({ id }) => id === selectedChart);
    return venue
      ? venue.categories.map((c) => ({
          ...c,
          disabled: tickets.filter((t) => t.categoryId === c.id).length === categoryTypes.length,
        }))
      : [];
  }, [chartsData, selectedChart, tickets, categoryTypes]);

  const selectedCategoryTypes = useMemo(() => {
    return categoryTypes.map(({ id, label, default: is_default }) => ({
      id,
      label,
      is_default,
      disabled: !!tickets.find((t) => t.categoryId === selectedCategory && t.type_id === id),
    }));
  }, [selectedCategory, tickets, categoryTypes]);

  const onPortrait = useCallback(
    (file) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result;
        setSelectedPortrait(base64);
      };
      reader.onerror = () => {
        console.error('Error reading file');
      };
      reader.readAsDataURL(file);
    },
    [setSelectedPortrait]
  );

  const onCover = useCallback(
    (file) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result;
        setSelectedCover(base64);
      };
      reader.onerror = () => {
        console.error('Error reading file');
      };
      reader.readAsDataURL(file);
    },
    [setSelectedCover]
  );
  const chartsDataError = useSelector(({ charts: { error } }) => error);

  const playsData = useSelector((state) => state.plays.data);
  const playsDataLoading = useSelector(({ plays: { isLoading } }) => isLoading);
  const playsDataError = useSelector(({ plays: { error } }) => error);

  const companiesData = useSelector((state) =>
    state.organizers.organizerCompanies.map(({ id, name, is_default }) => ({ id, name, is_default }))
  );
  const companiesDataLoading = useSelector((state) => state.organizers.isLoading);
  const companiesDataError = useSelector((state) => state.organizers.error);

  const isLoading = useSelector((state) => state.events.isLoading);

  const createDisabled = useMemo(
    () =>
      isLoading ||
      !selectedChart ||
      !selectedPlay ||
      (hasEventDate && (!selectedDate || dayjs(selectedDate).isBefore(dayjs()))) ||
      !selectedCompany,
    [isLoading, selectedChart, selectedPlay, hasEventDate, selectedDate, selectedCompany]
  );

  const updateDisabled = useMemo(
    () =>
      isLoading ||
      !selectedChart ||
      !selectedPlay ||
      (hasEventDate && (!selectedDate || dayjs(selectedDate).isBefore(dayjs()))),
    [isLoading, selectedChart, selectedPlay, hasEventDate, selectedDate]
  );

  const toggleOpenWarning = () => {
    setWarningOpen(!warningOpen);
  };

  const onTogglePromotion = (type) => {
    if (type === PromotionType.ONE_PLUS_ONE) {
      return (event) => {
        const checked = event.target.checked;
        if (checked) {
          setThreePlusOne(false);
        }

        setOnePlusOne(checked);
      };
    }

    if (type === PromotionType.THREE_PLUS_ONE) {
      return (event) => {
        const checked = event.target.checked;
        if (checked) {
          setOnePlusOne(false);
        }

        setThreePlusOne(checked);
      };
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategoryTypes();
      dispatch(fetchCharts());
      dispatch(fetchPlays({ status: 'ACTIVE' }));
      dispatch(fetchOrganizerCompanies());
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (playsDataError || chartsDataError || companiesDataError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [playsDataError, chartsDataError, companiesDataError, t]);

  useEffect(() => {
    dispatch(fetchArtists());
    if ((event && type === EventsDrawerType.UPDATE) || (event && type === EventsDrawerType.COPY)) {
      const {
        charts_id,
        plays_id,
        date,
        status,
        premiere,
        companies_id,
        has_event_date,
        is_seatsio_chart_visible,
        is_one_plus_one_free,
        is_three_plus_one_free,
        national_premiere,
        second_representation,
        index_for_search,
        title,
        name_en,
        name_de,
        name_hu,
        description,
        description_en,
        description_de,
        description_hu,
        length,
        cover_image_url,
        portrait_image_url,
        cast,
        has_breaks,
        attributes,
      } = event;

      setSelectedCompany(companies_id);
      setSelectedChart(charts_id);
      setSelectedPlay(plays_id);
      setHasEventDate(has_event_date !== 0);
      setSelectedDate(dayjs(date));
      setSelectedStatus(status);
      setIsSeatsioChartVisible(Boolean(is_seatsio_chart_visible));
      setIndex(Boolean(index_for_search));
      setPremiere(Boolean(premiere));
      setNationalPremiere(Boolean(national_premiere));
      setSecondRepresentation(Boolean(second_representation));
      setOnePlusOne(Boolean(is_one_plus_one_free));
      setThreePlusOne(Boolean(is_three_plus_one_free));
      setPlayName(title);
      setPlayNameEn(name_en);
      setPlayNameDe(name_de);
      setPlayNameHu(name_hu);
      setDescription(description || '');
      setDescriptionEn(description_en || '');
      setDescriptionDe(description_de || '');
      setDescriptionHu(description_hu || '');
      setSelectedCoverUrl(cover_image_url);
      setSelectedPortraitUrl(portrait_image_url);
      setSelectedArtists(
        cast.map(({ first_name, last_name, ...rest }) => ({
          label: `${first_name} ${last_name}`,
          ...rest,
        }))
      );
      setAttributes(
        attributes.map((attr) => ({
          id: nanoid(),
          attributeName: attr.attribute_name,
          attributeValue: attr.attribute_value,
          attributeName_en: attr.attribute_name_en,
          attributeValue_en: attr.attribute_value_en,
          attributeName_de: attr.attribute_name_de,
          attributeValue_de: attr.attribute_value_de,
          attributeName_hu: attr.attribute_name_hu,
          attributeValue_hu: attr.attribute_value_hu,
        }))
      );
      setLength(String(length || ''));
      setHasPause(Boolean(has_breaks));

      setTickets(
        event.tickets.map(
          ({
            id,
            type_id,
            type_label,
            is_default_type,
            color,
            price,
            description,
            discount,
            name,
            categories_id: categoryId,
          }) => ({
            id,
            type_id,
            type_label,
            is_default_type,
            color,
            price,
            description,
            discount,
            name,
            categoryId,
            canRemove: type !== EventsDrawerType.UPDATE,
          })
        )
      );
    }
  }, [event, type, dispatch]);

  useEffect(() => {
    if (selectedPlay && playsData.length && playChanged) {
      let selectedEventData = playsData.find((play) => selectedPlay === play.id);
      const {
        name,
        name_en,
        name_de,
        name_hu,
        description,
        description_en,
        description_de,
        description_hu,
        length,
        cover_image_url,
        portrait_image_url,
        cast,
        has_breaks,
        attributes,
      } = selectedEventData;
      setPlayName(name);
      setPlayNameEn(name_en);
      setPlayNameDe(name_de);
      setPlayNameHu(name_hu);
      setDescription(description || '');
      setDescriptionEn(description_en || '');
      setDescriptionDe(description_de || '');
      setDescriptionHu(description_hu || '');
      setSelectedCoverUrl(cover_image_url);
      setSelectedPortraitUrl(portrait_image_url);
      setSelectedArtists(
        cast.map(({ first_name, last_name, ...rest }) => ({
          label: `${first_name} ${last_name}`,
          ...rest,
        }))
      );
      setLength(String(length || ''));
      setHasPause(Boolean(has_breaks));
      setAttributes(
        attributes.map((attr) => ({
          id: nanoid(),
          attributeName: attr.attribute_name,
          attributeValue: attr.attribute_value,
          attributeName_en: attr.attribute_name_en,
          attributeValue_en: attr.attribute_value_en,
          attributeName_de: attr.attribute_name_de,
          attributeValue_de: attr.attribute_value_de,
          attributeName_hu: attr.attribute_name_hu,
          attributeValue_hu: attr.attribute_value_hu,
        }))
      );
    }
  }, [selectedPlay, playsData, playChanged, type]);

  const onDrawerClose = () => {
    setSelectedLanguage('ro');
    setSelectedChart('');
    setSelectedPlay('');
    setSelectedDate(null);
    setIsSeatsioChartVisible(true);
    setHasEventDate(true);
    setSelectedCategory('');
    setSelectedType('');
    setPrice('');
    setSelectedStatus('');
    setTickets([]);
    setOnePlusOne(false);
    setSelectedCompany('');
    setIndex(true);
    setPremiere(false);
    setNationalPremiere(false);
    setSecondRepresentation(false);
    setCategoryTypes([]);
    setPlayName('');
    setPlayNameEn('');
    setPlayNameDe('');
    setPlayNameHu('');
    setDescription('');
    setDescriptionEn('');
    setDescriptionDe('');
    setDescriptionHu('');
    setSelectedCoverUrl('');
    setSelectedPortraitUrl('');
    setSelectedCover(null);
    setSelectedPortrait(null);
    setSelectedArtist(null);
    setSelectedArtists([]);
    setAttributes([]);
    setIndex(true);
    setLength('');
    setHasPause(false);
    setOpenPlay(false);
    setPlayChanged(false);
    onClose();
  };

  const addTicket = () => {
    const ids = tickets.map(({ id }) => id);
    const nextId = Math.max(...(ids.length ? ids : [-1])) + 1;
    const { id, color, name } = selectedChartCategories.find(({ id }) => id === selectedCategory);
    const { id: typeId, label, is_default } = selectedCategoryTypes.find(({ id }) => id === selectedType);
    setTickets((current) => [
      ...current,
      {
        id: nextId,
        type_id: typeId,
        type_label: label,
        is_default_type: is_default,
        categoryId: id,
        name,
        color,
        price: Number(price),
        description: categoryDescription,
        discount: 0,
        canRemove: true,
      },
    ]);
    setSelectedCategory('');
    setSelectedType('');
    setPrice('');
    setCategoryDescription('');
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = selectedArtists.findIndex((a) => a.id === active.id);
    const newIndex = selectedArtists.findIndex((a) => a.id === over.id);
    setSelectedArtists((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleDragEndAttributes = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setAttributes((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);

      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const removeTicket = (idToRemove) => {
    const idx = tickets.findIndex(({ id }) => idToRemove === id);
    const newState = [...tickets];
    newState.splice(idx, 1);
    setTickets(newState);
  };

  const onUpdatePrice = (event) => {
    const value = event.target.value;
    if (validator.isDecimal(value, { decimal_digits: '0,2' }) || value === '') {
      setPrice(value);
    }
  };

  const addArtist = () => {
    setSelectedArtists((current) => [...current, { ...selectedArtist, role }]);
    setRole('');
    setSelectedArtist(null);
  };

  const addAttribute = () => {
    setAttributes((current) => [...current, { id: nanoid(), attributeName, attributeValue }]);
    setAttributeName('');
    setAttributeValue('');
  };

  const removeAttribute = (idToRemove) => {
    const idx = attributes.findIndex(({ id }) => idToRemove === id);
    const newState = [...attributes];
    newState.splice(idx, 1);
    setAttributes(newState);
  };

  const updateAttributeName = (itemId, newValue, lang) => {
    setAttributes((prev) =>
      prev.map((attr) =>
        attr.id === itemId
          ? {
              ...attr,
              ...(lang === 'ro' ? { attributeName: newValue } : { [`attributeName_${lang}`]: newValue }),
            }
          : attr
      )
    );
  };

  const updateAttributeValue = (itemId, newValue, lang) => {
    setAttributes((prev) =>
      prev.map((attr) =>
        attr.id === itemId
          ? {
              ...attr,
              ...(lang === 'ro' ? { attributeValue: newValue } : { [`attributeValue_${lang}`]: newValue }),
            }
          : attr
      )
    );
  };

  const updateArtistRole = (artistId, newRole, lang) => {
    setSelectedArtists((prev) =>
      prev.map((artist) =>
        artist.id === artistId
          ? {
              ...artist,
              ...(lang === 'ro' ? { role: newRole } : { [`role_${lang}`]: newRole }),
            }
          : artist
      )
    );
  };

  const removeArtist = (idToRemove) => {
    const idx = selectedArtists.findIndex(({ id }) => idToRemove === id);
    const newState = [...selectedArtists];
    newState.splice(idx, 1);
    setSelectedArtists(newState);
  };

  const getOptionDisabled = (option) => {
    const { id } = option;
    return !!selectedArtists.find((artist) => artist.id === id);
  };

  const onCreateEvent = () => {
    const payload = {
      eventId: !playChanged ? event.id : null,
      date: hasEventDate ? selectedDate.toISOString() : null,
      has_event_date: hasEventDate,
      play_name: playName,
      play_name_en: playNameEn,
      play_name_de: playNameDe,
      play_name_hu: playNameHu,
      chart_id: selectedChart,
      play_id: selectedPlay,
      company_id: selectedCompany,
      is_seatsio_chart_visible: isSeatsioChartVisible,
      is_one_plus_one_free: onePlusOne,
      is_three_plus_one_free: threePlusOne,
      index_for_search: index,
      premiere: premiere,
      national_premiere: nationalPremiere,
      second_representation: secondRepresentation,
      category_pricings: tickets.map(({ type_id, categoryId, price, description }) => ({
        typeId: type_id,
        categoryId,
        price,
        description,
      })),
      description: description.trim() || undefined,
      description_en: descriptionEn.trim() || undefined,
      description_de: descriptionDe.trim() || undefined,
      description_hu: descriptionHu.trim() || undefined,
      length: length,
      portrait: selectedPortrait,
      cover: selectedCover,
      cover_image_id: selectedCoverUrl.split('/').pop(),
      portrait_image_id: selectedPortraitUrl.split('/').pop(),
      cast: selectedArtists.map(({ id, role, role_en, role_de, role_hu }) => ({ id, role, role_en, role_de, role_hu })),
      has_breaks: hasPause,
      attributes: attributes,
    };
    dispatch(createEvent(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('Event created successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  const onUpdateEvent = () => {
    const payload = {
      eventId: event.id,
      status: selectedStatus,
      date: hasEventDate ? selectedDate.toISOString() : null,
      play_name: playName,
      play_name_en: playNameEn,
      play_name_de: playNameDe,
      play_name_hu: playNameHu,
      play_id: selectedPlay,
      is_seatsio_chart_visible: isSeatsioChartVisible,
      index_for_search: index,
      premiere: premiere,
      national_premiere: nationalPremiere,
      second_representation: secondRepresentation,
      category_pricings: tickets
        .filter((c) => c.canRemove === true)
        .map(({ type_id, categoryId, price, description }) => ({
          typeId: type_id,
          categoryId,
          price,
          description,
        })),
      description: description.trim() || undefined,
      description_en: descriptionEn.trim() || undefined,
      description_de: descriptionDe.trim() || undefined,
      description_hu: descriptionHu.trim() || undefined,
      length: length,
      portrait: selectedPortrait,
      cover: selectedCover,
      cover_image_id: selectedCoverUrl.split('/').pop(),
      portrait_image_id: selectedPortraitUrl.split('/').pop(),
      cast: selectedArtists.map(({ id, role, role_en, role_de, role_hu }) => ({ id, role, role_en, role_de, role_hu })),
      has_breaks: hasPause,
      attributes: attributes,
    };
    dispatch(updateEvent(payload)).then(({ meta }) => {
      if (meta.requestStatus === 'fulfilled') {
        toast.success(t('Event updated successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  const onDeleteEvent = () => {
    const payload = {
      eventId: event.id,
    };
    dispatch(deleteEvent(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('Event deleted successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        toggleOpenWarning();
        onDrawerClose();
        navigate(-1);
      }
    });
  };

  return (
    <Drawer anchor="right" open={open}>
      <Toolbar />
      <Toolbar>
        <Typography variant="button">
          {type === EventsDrawerType.CREATE
            ? t('Create Event')
            : type === EventsDrawerType.UPDATE
            ? t('Edit Event')
            : t('Copy Event')}
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ width: 400, pr: 2, pl: 2 }}>
        <Box sx={{ pb: 2 }} component="form">
          {type === EventsDrawerType.CREATE && (
            <FormControl
              required
              variant="standard"
              margin="normal"
              size="small"
              fullWidth
              disabled={chartsDataLoading}
            >
              <InputLabel id="chart-label">{t('Chart Map')}</InputLabel>
              <Select
                value={selectedChart}
                onChange={(event) => setSelectedChart(event.target.value)}
                labelId="chart-label"
                id="chart"
              >
                {chartsData.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormGroup sx={{ display: 'block', mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isSeatsioChartVisible}
                  onChange={(event) => setIsSeatsioChartVisible(event.target.checked)}
                />
              }
              label={t('Show Seats.io chart')}
            />
          </FormGroup>
          <FormControl variant="standard" required margin="normal" size="small" fullWidth disabled={playsDataLoading}>
            <InputLabel id="play-label">{t('Play')}</InputLabel>
            <Select
              value={selectedPlay}
              onChange={(event) => {
                setPlayChanged(true);
                setSelectedPlay(event.target.value);
              }}
              labelId="play-label"
              id="play"
            >
              {playsData.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ mt: 0.5 }} variant="button">
              {t('Play details')}
            </Typography>
            <IconButton
              aria-label="expand row"
              disabled={selectedPlay === ''}
              size="small"
              onClick={() => setOpenPlay(!openPlay)}
            >
              {openPlay ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={openPlay} timeout="auto" unmountOnExit>
            <LanguageSelector language={selectedLanguage} setLanguage={setSelectedLanguage} isDrawer={true} />
            <Stack spacing={2}>
              <TextField
                value={playNameValue ?? ''}
                onChange={(event) => setPlayNameValue(event.target.value)}
                fullWidth
                required={selectedLanguage === 'ro'}
                variant="standard"
                margin="normal"
                size="small"
                name="event_name"
                label={t('Event name')}
              />
              <TextField
                multiline
                minRows={5}
                maxRows={10}
                value={descriptionValue}
                onChange={(event) => setDescriptionValue(event.target.value.replace('\n', '\r\n'))}
                fullWidth
                variant="standard"
                margin="normal"
                size="small"
                name="description"
                label={t('Details')}
              />
              <Box mt={2}>
                <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
                  {t('PROPERTIES')}
                </Typography>
                <TextField
                  value={attributeName}
                  onChange={(event) => setAttributeName(event.target.value)}
                  fullWidth
                  variant="standard"
                  margin="normal"
                  size="small"
                  name="attributeName"
                  label={t('Attribute Name')}
                  disabled={selectedLanguage !== DEFAULT_LANGUAGE}
                  inputProps={{ maxLength: 45 }}
                />
                <Button
                  sx={{ marginTop: 2 }}
                  onClick={addAttribute}
                  disabled={!attributeName || selectedLanguage !== DEFAULT_LANGUAGE}
                  fullWidth
                  variant="text"
                  size="small"
                >
                  {t('Add')}
                </Button>
                <List>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndAttributes}>
                    <SortableContext items={attributes.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                      {attributes.map((item) => (
                        <SortableItemAttribute
                          key={item.id}
                          item={item}
                          removeAttribute={removeAttribute}
                          updateAttributeName={updateAttributeName}
                          updateAttributeValue={updateAttributeValue}
                          selectedLanguage={selectedLanguage}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </List>
              </Box>
              <TextField
                value={length}
                onChange={(event) => setLength(event.target.value)}
                fullWidth
                variant="standard"
                margin="normal"
                size="small"
                name="length"
                label={t('Length')}
                helperText={t('Expressed in minutes')}
              />
              <FormGroup sx={{ display: 'block', mt: 2, mb: 2 }}>
                <FormControlLabel
                  control={<Switch checked={hasPause} onChange={(event) => setHasPause(event.target.checked)} />}
                  label={t('With breaks')}
                />
              </FormGroup>
              <Box mt={2}>
                <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
                  {t('Cast *')}
                </Typography>
                <Autocomplete
                  autoComplete
                  getOptionDisabled={getOptionDisabled}
                  value={selectedArtist}
                  disabled={selectedLanguage !== DEFAULT_LANGUAGE}
                  onChange={(event, newValue) => {
                    setSelectedArtist(newValue);
                  }}
                  disablePortal
                  options={artistsOptions}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      name="artists"
                      label={t('Search artist')}
                      placeholder={t('Name...')}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                />
                <TextField
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  fullWidth
                  disabled={selectedLanguage !== DEFAULT_LANGUAGE}
                  variant="standard"
                  margin="normal"
                  size="small"
                  name="role"
                  label={t('Artist role')}
                  inputProps={{ maxLength: 45 }}
                />
                <Button
                  sx={{ marginTop: 2 }}
                  onClick={addArtist}
                  disabled={!selectedArtist || selectedLanguage !== DEFAULT_LANGUAGE}
                  fullWidth
                  variant="text"
                  size="small"
                >
                  {t('Add')}
                </Button>
                <List>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={selectedArtists.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                      {selectedArtists.map((artist) => (
                        <SortableItemCast
                          key={artist.id}
                          artist={artist}
                          removeArtist={removeArtist}
                          updateArtistRole={updateArtistRole}
                          selectedLanguage={selectedLanguage}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </List>
              </Box>
              <Box mt={2}>
                <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
                  {t('Portrait Image *')}
                </Typography>
                <FileDropzone
                  sx={{ height: 300 }}
                  previewUrl={selectedPortraitUrl}
                  preview
                  fileTypes={ACCEPTED_FORMATS}
                  maxSize={MAX_IMAGE_SIZE_1MB}
                  onDrop={onPortrait}
                />
              </Box>
              <Box mt={2} mb={2}>
                <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
                  {t('Cover Image *')}
                </Typography>
                <FileDropzone
                  sx={{ height: 300 }}
                  previewUrl={selectedCoverUrl}
                  preview
                  fileTypes={ACCEPTED_FORMATS}
                  maxSize={MAX_IMAGE_SIZE_1MB}
                  onDrop={onCover}
                />
              </Box>
            </Stack>
          </Collapse>
          {[EventsDrawerType.CREATE, EventsDrawerType.COPY].includes(type) && (
            <FormControl
              variant="standard"
              required
              margin="normal"
              size="small"
              fullWidth
              disabled={companiesDataLoading}
            >
              <InputLabel id="play-label">{t('Company')}</InputLabel>
              <Select
                value={selectedCompany}
                onChange={(event) => setSelectedCompany(event.target.value)}
                labelId="company-label"
                id="company"
              >
                {companiesData.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {type === EventsDrawerType.CREATE && (
            <FormGroup sx={{ display: 'block', mt: 2 }}>
              <FormControlLabel
                control={<Switch checked={hasEventDate} onChange={(event) => setHasEventDate(event.target.checked)} />}
                label={t('Has event date')}
              />
            </FormGroup>
          )}
          {!(type === EventsDrawerType.UPDATE && !hasEventDate) && (
            <DateTimePicker
              disablePast
              disabled={!hasEventDate}
              value={selectedDate}
              onChange={(value) => setSelectedDate(value)}
              slotProps={{
                textField: {
                  required: hasEventDate,
                  label: t('Date'),
                  fullWidth: true,
                  margin: 'normal',
                  variant: 'standard',
                },
              }}
            />
          )}
          {type === EventsDrawerType.UPDATE && (
            <FormControl variant="standard" margin="normal" size="small" fullWidth>
              <InputLabel id="event-status">{t('Status')}</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(event) => setSelectedStatus(event.target.value)}
                labelId="event-status"
                id="event-status"
              >
                <MenuItem value={'SELLING'}>{t('Selling')}</MenuItem>
                <MenuItem value={'NOT_SELLING'}>{t('Not selling')}</MenuItem>
                <MenuItem value={'ARCHIVED'}>{t('Archived')}</MenuItem>
              </Select>
            </FormControl>
          )}
          {[EventsDrawerType.CREATE, EventsDrawerType.COPY].includes(type) && (
            <FormGroup sx={{ display: 'block', mt: 2 }}>
              <FormControlLabel
                control={<Switch checked={onePlusOne} onChange={onTogglePromotion(PromotionType.ONE_PLUS_ONE)} />}
                label={t('Toggle 1+1 Free')}
              />
            </FormGroup>
          )}
          {/*[EventsDrawerType.CREATE, EventsDrawerType.COPY].includes(type) && (
            <FormGroup sx={{ display: 'block', mt: 2 }}>
              <FormControlLabel
                control={<Switch checked={threePlusOne} onChange={onTogglePromotion(PromotionType.THREE_PLUS_ONE)} />}
                label="Toggle 3+1 Free"
              />
            </FormGroup>
          )*/}
          <FormGroup sx={{ display: 'block', mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={premiere} onChange={(event) => setPremiere(event.target.checked)} />}
              label={t('Premiere')}
            />
          </FormGroup>
          <FormGroup sx={{ display: 'block', mt: 2 }}>
            <FormControlLabel
              control={
                <Switch checked={nationalPremiere} onChange={(event) => setNationalPremiere(event.target.checked)} />
              }
              label={t('National premiere')}
            />
          </FormGroup>
          <FormGroup sx={{ display: 'block', mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={secondRepresentation}
                  onChange={(event) => setSecondRepresentation(event.target.checked)}
                />
              }
              label={t('Second representation')}
            />
          </FormGroup>
        </Box>
        <Box component="form">
          <Typography variant="button">{t('Category pricings')}</Typography>
          <FormControl
            disabled={!selectedChart || !selectedChartCategories.length || chartsDataLoading}
            variant="standard"
            margin="normal"
            size="small"
            fullWidth
          >
            <InputLabel id="category-label">{t('Category')}</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
              labelId="category-label"
              id="category"
            >
              {selectedChartCategories.map(({ id, name, disabled }) => (
                <MenuItem disabled={disabled} key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            disabled={!selectedCategory || !selectedCategoryTypes.length}
            variant="standard"
            margin="normal"
            size="small"
            fullWidth
          >
            <InputLabel id="type-label">{t('Type')}</InputLabel>
            <Select
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
              labelId="type-label"
              id="type"
            >
              {selectedCategoryTypes.map(({ id, label, is_default, disabled }) => (
                <MenuItem disabled={disabled} key={id} value={id}>
                  {label}
                  {!!is_default && t(' (Default)')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            disabled={!selectedCategory || !selectedType}
            value={price}
            onChange={onUpdatePrice}
            variant="standard"
            margin="normal"
            size="small"
            fullWidth
            id="price"
            label={t('Price')}
          />
          <TextField
            disabled={!selectedCategory || !selectedType}
            value={categoryDescription}
            onChange={(event) => setCategoryDescription(event.target.value)}
            variant="standard"
            margin="normal"
            size="small"
            fullWidth
            helperText={t('Characters: {{count}}/{{max}}', {
              count: categoryDescription.length,
              max: CATEGORY_DESCRIPTION_MAX_LENGTH,
            })}
            inputProps={{ maxLength: CATEGORY_DESCRIPTION_MAX_LENGTH }}
            id="description"
            label={t('Description')}
          />
          <Button
            disabled={!selectedChart || !selectedChartCategories.length || !price || isNaN(price) || !selectedCategory}
            onClick={addTicket}
            fullWidth
            variant="text"
            size="small"
          >
            {t('Add')}
          </Button>
        </Box>
        <List>
          {tickets.map(
            ({ id, name, type_id, type_label, is_default_type, color, price, description, discount, canRemove }) => (
              <ListItem sx={{ borderLeft: `5px solid ${color}` }} key={`${id}-${type_id}`} divider>
                <ListItemText
                  sx={{ pr: 12 }}
                  primary={
                    <>
                      {name}
                      {!is_default_type && (
                        <Chip sx={{ maxWidth: 125, marginLeft: 1 }} size="small" label={type_label} />
                      )}
                    </>
                  }
                  secondary={description}
                />

                <ListItemSecondaryAction>
                  {(price - price * discount).toFixed(2) + t(' Lei')}
                  {canRemove && (
                    <IconButton onClick={() => removeTicket(id)} edge="end" aria-label="delete">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            )
          )}
        </List>
        <FormGroup sx={{ display: 'block', mt: 2, mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={index} onChange={(event) => setIndex(event.target.checked)} />}
            label={t('Index in main site')}
          />
        </FormGroup>
        {type === EventsDrawerType.UPDATE && (
          <>
            <Button disabled={event && !event.can_delete} onClick={toggleOpenWarning} fullWidth variant="outlined">
              {t('Delete')}
            </Button>
            <WarningDialog
              open={warningOpen}
              loading={isLoading}
              onCancel={toggleOpenWarning}
              onConfirm={onDeleteEvent}
              title={t('Warning')}
              content={t('Are you sure you want to delete this event?')}
            />
          </>
        )}
      </Box>
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Grid columnSpacing={2} container>
          <Grid xs={6} item>
            <Button fullWidth variant="outlined" onClick={onDrawerClose}>
              {t('Cancel')}
            </Button>
          </Grid>
          <Grid xs={6} item>
            <Button
              disabled={
                [EventsDrawerType.CREATE, EventsDrawerType.COPY].includes(type) ? createDisabled : updateDisabled
              }
              fullWidth
              variant="contained"
              onClick={[EventsDrawerType.CREATE, EventsDrawerType.COPY].includes(type) ? onCreateEvent : onUpdateEvent}
            >
              {type === EventsDrawerType.CREATE
                ? t('Create')
                : type === EventsDrawerType.UPDATE
                ? t('Update')
                : t('Copy')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}

export default EventsDrawer;
